import React, { useState, useEffect } from 'react';
import apiClient from '../utils/apiClient';
import Select from 'react-select';
import { SERVER_BASE } from '../config/server';
import { useTable, useSortBy, usePagination, useGlobalFilter } from 'react-table';
import * as XLSX from 'xlsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import Swal from 'sweetalert2';
import DateRangePickerComponent from './CustomDateRangePicker';
import moment from 'moment';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const yesNoOptions = [
  { label: 'Yes', value: 'Yes' },
  { label: 'No', value: 'No' },
  { label: 'Not Applicable', value: 'Not Applicable' },
];
const OpeningStockReport = ({ accessOfDepartments }) => {
  const [departments, setDepartments] = useState([]);
  const [approvedByDera, setApprovedByDera] = useState(null);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const code = localStorage.getItem('code');
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (accessOfDepartments) {
      const deptArray = accessOfDepartments
        .split(',')
        .map((dept) => ({ value: dept.trim(), label: dept.trim() }));
      setDepartments(deptArray);
    }
  }, [accessOfDepartments]);

  const getReport = async () => {
    if (!selectedDepartments || selectedDepartments.length === 0) {
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'warning',
        title: 'Please select at least one Department',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    setLoading(true);

    try {
      const res = await apiClient.get(SERVER_BASE + '/getOpeningStockItemsByApproval', {
        params: {
          departments: selectedDepartments.map(d => d.value).join(','), // 'Stores,Accounts'
        },
      });

      setReportData(res.data);
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns = React.useMemo(() => [
    {
      Header: "S.No",
      Cell: ({ row }) => row.index + 1,
    },
    { Header: 'Item', accessor: 'printName' },
    { Header: 'Department', accessor: 'department' },
    {
      Header: 'Quantity',
      accessor: 'totalQuantity',
      Cell: ({ value }) => {
        if (value == null) return '';
        return Number(value) % 1 === 0 ? Number(value) : Number(value).toFixed(2);
      }
    },
    { Header: 'UOM', accessor: 'uom' },
    {
      Header: 'Approved By Dera',
      accessor: 'approvedByDera',
      Cell: ({ value }) => (value === "Not Applicable" ? "N/A" : value),
    }
  ], []);

  const {
    getTableProps, getTableBodyProps, headerGroups, page,
    prepareRow, setGlobalFilter, state, nextPage, previousPage,
    canNextPage, canPreviousPage, pageOptions, setPageSize
  } = useTable(
    { columns, data: reportData, initialState: { pageSize: 10 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Opening Stock Report');

    // Header row with formatting
    worksheet.columns = [
      { header: 'Item', key: 'printName', width: 30 },
      { header: 'Department', key: 'department', width: 25 },
      { header: 'Quantity', key: 'totalQuantity', width: 15 },
      { header: 'UOM', key: 'uom', width: 15 },
    ];

    // Add rows
    reportData.forEach((row) => {
      worksheet.addRow({
        printName: row.printName,
        department: row.department,
        totalQuantity: row.totalQuantity,
        uom: row.uom,
      });
    });

    // Format header row
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

    // Apply border and alignment to all cells
    worksheet.eachRow({ includeEmpty: false }, (row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          bottom: { style: 'thin' },
          left: { style: 'thin' },
          right: { style: 'thin' },
        };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
      });
    });

    // Export the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `OpeningStockReport_${moment().format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <div className="formcontainer mt-3">
      <h5>Department Opening Stock</h5>
      <div className="mb-4">
        <div className="row align-items-end mb-3">
          <div className="col-md-7">
            <label className="form-label">Departments</label>
            <Select
              isMulti
              options={departments}
              value={selectedDepartments}
              onChange={setSelectedDepartments}
            />
          </div>
          {/* <div className="col-md-3">
            <label className="form-label">Approved By Dera</label>
            <Select
              isMulti
              options={yesNoOptions}
              value={approvedByDera}
              onChange={setApprovedByDera}
            />
          </div> */}
          <div className="col-md-2">
            <button
              className="btn btn-outline-success w-100"
              onClick={getReport}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Get Report'}
            </button>
          </div>
        </div>
      </div>
      <div className="row align-items-center mb-2">
        <div className="col-md-8">
          <input
            className="form-control"
            placeholder="Search..."
            onChange={e => setGlobalFilter(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <input
            className="form-control"
            placeholder="Search..."
            value={`Count : ${reportData?.length}`}
          />
        </div>
        {reportData.length > 0 && (
          <div className="col-md-3 text-end">
            <button className="btn btn-success w-100" onClick={exportToExcel}>
              Export to Excel
            </button>
          </div>
        )}
      </div>


      <table
        style={{ fontSize: '15px', verticalAlign: 'baseline' }}
        className="table table-striped table-bordered"
        {...getTableProps()}
      >
        <thead className="thead-dark">
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => (
                  <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="row mt-3 align-items-center">
        <div className="col-md-4 d-flex justify-content-start">
          <button className="btn btn-outline-secondary me-2" onClick={() => previousPage()} disabled={!canPreviousPage}>
            Previous
          </button>
          <button className="btn btn-outline-secondary" onClick={() => nextPage()} disabled={!canNextPage}>
            Next
          </button>
        </div>
        <div className="col-md-4 text-center">
          <span>
            Page {state.pageIndex + 1} of {pageOptions.length}
          </span>
        </div>
        <div className="col-md-4 d-flex justify-content-end align-items-center">
          <label className="me-2 mb-0">Rows per page:</label>
          <select
            className="form-select w-auto"
            value={state.pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>


    </div>
  );
}

export default OpeningStockReport;
